import {
  collection,
  query,
  orderBy,
  limit as qlimit,
  where,
  getDocs,
  getDocsFromCache,
  writeBatch,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  documentId,
  Timestamp,
  startAfter,
  type QueryDocumentSnapshot,
  type DocumentData,
  type QueryConstraint,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { todayStr, parseDateStr, addDays as addDaysStr } from '@/lib/dates'
import { appEvents, WEIGHT_HISTORY_CHANGED } from '@/lib/events'
import type { DayTargets, WeightPoint } from '@/kidney/dailyTargets'
import type { TimestampLike } from '@/kidney/dayPerformance'
import type { NutrientCoverage, TrackedNutrientKey } from '@/types'
import { dayIronCoverage, ironPeriodAverage, type CoverageEntryLike } from '@/lib/nutrients'
import { averagePralWithCoverage, pralItemsFromEntries, sumPral, type PralPeriodAverage, type PralEntryLike, type StoredDayPral } from '@/kidney/pral'

/**
 * The PERSISTED `kpal_users/{uid}/dailySummaries/{YYYY-MM-DD}` document —
 * mirrors mobile types/diary.ts DaySummaryDoc. 4.34 adds `totals` (every
 * tracked nutrient, trusted only while `totalsAt` equals `updatedAt`),
 * `targets` (the day's snapshot, written only while the date was today) and
 * `notesUpdatedAt` (note-only saves leave `updatedAt` alone).
 */
export interface DaySummaryDoc {
  date: string
  totalItems: number
  sodium: number
  potassium: number
  phosphorus: number
  protein: number
  fluid: number
  performance: 'none' | 'green' | 'yellow' | 'red'
  dailyNotes?: string
  totals?: Partial<Record<TrackedNutrientKey, number>> | null
  totalsAt?: TimestampLike | null
  /** 4.35: PRAL sum + coverage, trusted only while `pralAt` equals `updatedAt` (isPralFresh). */
  pral?: StoredDayPral | null
  pralAt?: TimestampLike | null
  updatedAt?: TimestampLike | null
  notesUpdatedAt?: TimestampLike | null
  targets?: DayTargets | null
  targetsAt?: TimestampLike | null
}

const tsOrNull = (v: unknown): TimestampLike | null =>
  v instanceof Timestamp ? { seconds: v.seconds, nanoseconds: v.nanoseconds } : null

/** One place that shapes a summary snapshot into DaySummaryDoc. */
export function daySummaryFromData(date: string, data: DocumentData): DaySummaryDoc {
  return {
    date,
    totalItems: data.totalItems || 0,
    sodium: data.sodium || 0,
    potassium: data.potassium || 0,
    phosphorus: data.phosphorus || 0,
    protein: data.protein || 0,
    fluid: data.fluid || 0,
    performance: (data.performance as DaySummaryDoc['performance']) || 'none',
    dailyNotes: (data.dailyNotes as string) || undefined,
    totals: (data.totals as DaySummaryDoc['totals']) ?? null,
    totalsAt: tsOrNull(data.totalsAt),
    pral: (data.pral as StoredDayPral | undefined) ?? null,
    pralAt: tsOrNull(data.pralAt),
    updatedAt: tsOrNull(data.updatedAt),
    notesUpdatedAt: tsOrNull(data.notesUpdatedAt),
    targets: (data.targets as DayTargets | undefined) ?? null,
    targetsAt: tsOrNull(data.targetsAt),
  }
}

/**
 * Daily summaries for an exact date range (doc id = YYYY-MM-DD). This is the
 * SAME source the app's Nutrient Trends chart reads — values can differ
 * slightly from raw-entry sums, so the charts must share it to match.
 */
export async function fetchSummariesRange(
  uid: string,
  startDate: string,
  endDate: string,
): Promise<DaySummaryDoc[]> {
  const q = query(
    collection(db, 'kpal_users', uid, 'dailySummaries'),
    where(documentId(), '>=', startDate),
    where(documentId(), '<=', endDate),
  )
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => daySummaryFromData(d.id, d.data()))
    .sort((a, b) => (a.date < b.date ? -1 : 1))
}

/**
 * Live day docs for a date range (4.36 H1/H2 — the Progress heatmap and PRAL
 * trend). Same documentId range as fetchSummariesRange, as an onSnapshot: this
 * tab's pending writes are merged in and the listener re-fires on
 * acknowledgement. `pending` is the doc's own metadata.hasPendingWrites (the
 * calendar's pending-timestamp rule reads it). Read-only; errors are surfaced.
 */
export function subscribeSummariesRange(
  uid: string,
  startDate: string,
  endDate: string,
  onDocs: (rows: { doc: DaySummaryDoc; pending: boolean }[]) => void,
  onError: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, 'kpal_users', uid, 'dailySummaries'),
    where(documentId(), '>=', startDate),
    where(documentId(), '<=', endDate),
  )
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snap) =>
      onDocs(snap.docs.map((d) => ({ doc: daySummaryFromData(d.id, d.data()), pending: d.metadata.hasPendingWrites }))),
    (error) => {
      console.error('Summaries range listener failed:', error)
      onError(error)
    },
  )
}

/** Most-recent N daily summaries (doc id = YYYY-MM-DD sorts chronologically). */
export async function fetchRecentSummaries(uid: string, days = 30): Promise<DaySummaryDoc[]> {
  const q = query(
    collection(db, 'kpal_users', uid, 'dailySummaries'),
    orderBy(documentId(), 'desc'),
    qlimit(days),
  )
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => daySummaryFromData(d.id, d.data()))
    .reverse() // chronological ascending
}

/**
 * Every weight entry as a WeightPoint for per-day target backfill (4.34).
 * Paginated on the existing `(date asc, timestamp desc)` composite index with
 * document cursors until exhausted — no cap (mobile parity).
 */
export async function fetchAllWeightPoints(uid: string, pageSize = 500): Promise<WeightPoint[]> {
  const col = collection(db, 'kpal_users', uid, 'weightHistory')
  const points: WeightPoint[] = []
  let cursor: QueryDocumentSnapshot<DocumentData> | null = null
  for (;;) {
    const constraints: QueryConstraint[] = [orderBy('date', 'asc'), orderBy('timestamp', 'desc'), qlimit(pageSize)]
    if (cursor) constraints.push(startAfter(cursor))
    const q: Query<DocumentData> = query(col, ...constraints)
    const snap: QuerySnapshot<DocumentData> = await getDocs(q)
    for (const d of snap.docs) {
      const data = d.data()
      const weightKg = data.weightKg ?? data.weight
      if (typeof data.date !== 'string' || typeof weightKg !== 'number') continue
      const point: WeightPoint = { date: data.date, weightKg }
      if (data.timestamp instanceof Timestamp) point.timestamp = data.timestamp.toMillis()
      if (data.updatedAt instanceof Timestamp) point.updatedAt = data.updatedAt.toMillis()
      points.push(point)
    }
    if (snap.docs.length < pageSize) break
    cursor = snap.docs[snap.docs.length - 1]
  }
  return points
}

/**
 * Per-day nutrient totals computed straight from diary entries — the ground
 * truth mobile uses for trends/insights (dailySummaries docs may be missing
 * for days logged before the summary writer existed).
 */
export interface DayAggregate {
  date: string
  totalItems: number
  /** Non-fluid entries (the report's "meals" — mobile aggregateByDay.mealCount). */
  mealCount: number
  sodium: number
  potassium: number
  phosphorus: number
  protein: number
  fluid: number
  calories: number
  carbs: number
  sugars: number
  fiber: number
  fat: number
  saturatedFat: number
  magnesium: number
  calcium: number
  vitaminK: number
  oxalate: number
  /**
   * 4.39: iron coverage of the day's entries (known subtotal + how many of the entries with
   * nutrients carry a value). Unknown is never summed as 0 — mobile report-generator parity.
   */
  iron: NutrientCoverage
}

export interface TopSource {
  name: string
  total: number
  count: number
}

export interface ReportData {
  days: DayAggregate[]
  topSources: { sodium: TopSource[]; potassium: TopSource[]; phosphorus: TopSource[] }
  /** "date|mealType" keys for meals containing food — binder adherence denominator. */
  mealPresence: string[]
  /**
   * 4.35 PRAL: average over the days whose every food could be judged — computed from the
   * ENTRIES (never from a stored day value). A day with an unknown food is a logged day that
   * is left out, never a zero (mobile report-generator parity).
   */
  pral: PralPeriodAverage
  /** 4.39: iron average over the COMPLETE days only (every entry has a value), with the day counts. */
  iron: { value: number | null; completeDays: number; loggedDays: number }
}

function emptyDay(date: string): DayAggregate {
  return {
    date, totalItems: 0, mealCount: 0, sodium: 0, potassium: 0, phosphorus: 0, protein: 0, fluid: 0,
    calories: 0, carbs: 0, sugars: 0, fiber: 0, fat: 0, saturatedFat: 0, magnesium: 0, calcium: 0, vitaminK: 0, oxalate: 0,
    iron: { known: 0, total: 0, value: 0 },
  }
}

async function fetchEntriesRange(uid: string, startDate: string, endDate: string) {
  // IMPORTANT: orderBy('date') alone needs a composite index that is NOT
  // deployed — this exact shape (date, timestamp) matches mobile's
  // getEntriesForDateRange and the existing (userId, date, timestamp) index.
  const q = query(
    collection(db, 'kpal_diary_entries'),
    where('userId', '==', uid),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
    orderBy('date', 'asc'),
    orderBy('timestamp', 'asc'),
  )
  return getDocs(q)
}

function aggregateDays(snap: Awaited<ReturnType<typeof fetchEntriesRange>>): DayAggregate[] {
  const byDate = new Map<string, DayAggregate>()
  const entriesOf = new Map<string, CoverageEntryLike[]>()
  for (const d of snap.docs) {
    const e = d.data()
    const date = e.date as string
    const list = entriesOf.get(date)
    if (list) list.push(e as CoverageEntryLike)
    else entriesOf.set(date, [e as CoverageEntryLike])
    const agg = byDate.get(date) || emptyDay(date)
    const n = (e.nutrients || {}) as Record<string, number>
    agg.totalItems += 1
    if (!e.isFluid) agg.mealCount += 1
    agg.sodium += n.sodiumMg || 0
    agg.potassium += n.potassiumMg || 0
    agg.phosphorus += n.phosphorusMg || 0
    agg.protein += n.proteinG || 0
    // Mobile aggregateByDay counts fluid only from isFluid entries.
    if (e.isFluid && e.fluidMl) agg.fluid += e.fluidMl as number
    agg.calories += n.calories || 0
    agg.carbs += n.carbsG || 0
    agg.sugars += n.sugarsG || 0
    agg.fiber += n.fiberG || 0
    agg.fat += n.fatG || 0
    agg.saturatedFat += n.saturatedFatG || 0
    agg.magnesium += n.magnesiumMg || 0
    agg.calcium += n.calciumMg || 0
    agg.vitaminK += n.vitaminKMcg || 0
    agg.oxalate += n.oxalateMg || 0
    byDate.set(date, agg)
  }
  for (const [date, agg] of byDate) agg.iron = dayIronCoverage(entriesOf.get(date) ?? [])
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1))
}

export async function fetchDayAggregates(
  uid: string,
  startDate: string,
  endDate: string,
): Promise<DayAggregate[]> {
  return aggregateDays(await fetchEntriesRange(uid, startDate, endDate))
}

/**
 * Raw entries for the insights generator — mobile generateDiaryInsights reads
 * the trailing 30 days of entries (names included, for top-sodium/potassium
 * food callouts).
 */
export async function fetchInsightsEntries(uid: string): Promise<
  { date: string; name: string; isFluid?: boolean; fluidMl?: number;
    nutrients: { sodiumMg: number; potassiumMg: number; phosphorusMg: number; proteinG: number } }[]
> {
  const today = todayStr()
  const snap = await fetchEntriesRange(uid, addDaysStr(today, -30), today)
  return snap.docs.map((d) => {
    const e = d.data()
    const n = (e.nutrients || {}) as Record<string, number>
    return {
      date: e.date as string,
      name: (e.name as string) || '—',
      isFluid: !!e.isFluid,
      fluidMl: (e.fluidMl as number) || 0,
      nutrients: {
        sodiumMg: n.sodiumMg || 0,
        potassiumMg: n.potassiumMg || 0,
        phosphorusMg: n.phosphorusMg || 0,
        proteinG: n.proteinG || 0,
      },
    }
  })
}

/** Full report payload: per-day aggregates + top nutrient sources (mobile getTopSources). */
export async function fetchReportData(
  uid: string,
  startDate: string,
  endDate: string,
): Promise<ReportData> {
  const snap = await fetchEntriesRange(uid, startDate, endDate)

  const topFor = (pick: (n: Record<string, number>) => number): TopSource[] => {
    const map = new Map<string, TopSource>()
    for (const d of snap.docs) {
      const e = d.data()
      if (e.isFluid) continue
      const value = pick((e.nutrients || {}) as Record<string, number>) || 0
      if (value <= 0) continue
      const name = (e.name as string) || '—'
      const existing = map.get(name) || { name, total: 0, count: 0 }
      existing.total += value
      existing.count++
      map.set(name, existing)
    }
    return [...map.values()].sort((a, b) => b.total - a.total).slice(0, 5)
  }

  const mealPresence = new Set<string>()
  for (const d of snap.docs) {
    const e = d.data()
    if (!e.isFluid) mealPresence.add(`${e.date}|${e.mealType}`)
  }

  const entriesByDate = new Map<string, PralEntryLike[]>()
  for (const d of snap.docs) {
    const e = d.data()
    const date = e.date as string
    const list = entriesByDate.get(date)
    if (list) list.push(e as PralEntryLike)
    else entriesByDate.set(date, [e as PralEntryLike])
  }
  const pral = averagePralWithCoverage(
    [...entriesByDate.values()].map((dayEntries) => sumPral(pralItemsFromEntries(dayEntries))),
  )

  const days = aggregateDays(snap)
  return {
    pral,
    iron: ironPeriodAverage(days.map((d) => d.iron)),
    days,
    topSources: {
      sodium: topFor((n) => n.sodiumMg),
      potassium: topFor((n) => n.potassiumMg),
      phosphorus: topFor((n) => n.phosphorusMg),
    },
    mealPresence: [...mealPresence],
  }
}

/** Recent daily notes (dailySummaries docs with a non-empty dailyNotes) — mobile getAllDailyNotes. */
export async function fetchDailyNotes(
  uid: string,
  max = 1000, // mobile fetches up to 1000 — long-range reports must not drop notes
): Promise<{ date: string; notes: string }[]> {
  const q = query(
    collection(db, 'kpal_users', uid, 'dailySummaries'),
    where('dailyNotes', '!=', ''),
  )
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ date: d.id, notes: (d.data().dailyNotes as string) || '' }))
    .filter((n) => n.notes.trim().length > 0)
    .sort((a, b) => (a.date > b.date ? -1 : 1))
    .slice(0, max)
}

export interface RecentMeal {
  id: string
  photoThumbnailUrl?: string
  photoUrl?: string
  scanType?: string
  mealTime?: string
  kidneyAnalysis?: { kidneyImpactScore?: number; overallRisk?: string; entryName?: string }
  eatenAt?: Date
}

/**
 * Photo scans only (mobile scan-history filter: kidneyAnalysis && photoUrl) —
 * text/voice analyses have no photo and are excluded, like the app.
 */
export async function fetchRecentMeals(uid: string, max = 12): Promise<RecentMeal[]> {
  const q = query(
    collection(db, 'kpal_meals'),
    where('userId', '==', uid),
    orderBy('eatenAt', 'desc'),
    qlimit(max * 4),
  )
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => {
      const data = d.data()
      return {
        id: d.id,
        photoThumbnailUrl: data.photoThumbnailUrl,
        photoUrl: data.photoUrl,
        scanType: data.scanType,
        mealTime: data.mealTime,
        kidneyAnalysis: data.kidneyAnalysis,
        eatenAt: data.eatenAt instanceof Timestamp ? data.eatenAt.toDate() : undefined,
      } as RecentMeal
    })
    .filter((m) => m.kidneyAnalysis && (m.photoUrl || m.photoThumbnailUrl))
    .slice(0, max)
}

// ---------------------------------------------------------------------------
// Weight
// ---------------------------------------------------------------------------

export interface WeightEntry {
  id: string
  weightKg: number
  date?: string
  timestamp?: Date
  source?: string
  /** 4.44 R3: same-day tie-breaks (`shared/weightWinner.ts`); absent = oldest. */
  updatedAt?: Date
  createdAt?: Date
}

export function subscribeWeight(
  uid: string,
  onUpdate: (entries: WeightEntry[]) => void,
  max = 90,
): () => void {
  const q = query(
    collection(db, 'kpal_users', uid, 'weightHistory'),
    orderBy('timestamp', 'desc'),
    qlimit(max),
  )
  return onSnapshot(q, (snap) =>
    onUpdate(
      snap.docs
        .map((d) => {
          // 4.44 R3: 'estimate' — a reading just saved here (serverTimestamp
          // still pending) must count as the newest edit, not as "no time".
          const data = d.data({ serverTimestamps: 'estimate' })
          return {
            id: d.id,
            weightKg: data.weightKg ?? data.weight ?? 0,
            date: data.date,
            timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : undefined,
            source: data.source,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined,
          }
        })
        .reverse(),
    ),
  )
}

/** Weight entries dated within [startDate, endDate], chronological (report by range — 4.33). */
export async function fetchWeightRange(uid: string, startDate: string, endDate: string): Promise<WeightEntry[]> {
  const q = query(
    collection(db, 'kpal_users', uid, 'weightHistory'),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
    orderBy('date', 'asc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => {
    const data = d.data()
    return {
      id: d.id,
      weightKg: data.weightKg ?? data.weight ?? 0,
      date: data.date,
      timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : undefined,
      source: data.source,
    } as WeightEntry
  })
}

export async function deleteWeight(uid: string, entryId: string): Promise<void> {
  await deleteDoc(doc(db, 'kpal_users', uid, 'weightHistory', entryId))
  appEvents.emit(WEIGHT_HISTORY_CHANGED, uid)
}

/** Returns the id of the reading written, so a caller can mark it mirrored (4.36). */
export async function addWeight(uid: string, weightKg: number, date?: string): Promise<string> {
  // Mobile semantics: logging weight for a date that already has a MANUAL
  // entry UPDATES it (WeightLogModal finds `existing` and calls updateEntry) —
  // otherwise every correction multiplies weightHistory docs.
  const d = date || todayStr()
  const col = collection(db, 'kpal_users', uid, 'weightHistory')
  const existing = await getDocs(query(col, where('date', '==', d)))
  const manual = existing.docs.find((docSnap) => docSnap.data().source === 'manual')
  if (manual) {
    await updateDoc(manual.ref, { weightKg, mirroredToProfile: false, updatedAt: serverTimestamp() })
    appEvents.emit(WEIGHT_HISTORY_CHANGED, uid)
    return manual.id
  }
  // Shape must match mobile FirebaseWeightService.addEntry exactly: timestamp is
  // local midnight of the date (parseISO), and healthKitUUID is explicit null.
  const ref = await addDoc(col, {
    userId: uid,
    weightKg,
    date: d,
    source: 'manual',
    mirroredToProfile: false,
    healthKitUUID: null,
    timestamp: Timestamp.fromDate(parseDateStr(d)),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  appEvents.emit(WEIGHT_HISTORY_CHANGED, uid)
  return ref.id
}

/** Record that this reading is the one that moved kidneyProfile.weight (4.36). */
export async function markWeightMirrored(uid: string, entryId: string): Promise<void> {
  await updateDoc(doc(db, 'kpal_users', uid, 'weightHistory', entryId), { mirroredToProfile: true })
}

/** A Settings weight: the day's manual reading plus, optionally, profile leaves (4.38 F11). */
export interface ManualWeightInput {
  weightKg: number
  /** 'YYYY-MM-DD' — the reading's day. */
  date: string
  /**
   * Dotted `kidneyProfile.*` leaf paths committed in the SAME batch as the
   * reading (Rule 7: never a whole kidneyProfile map).
   */
  profileLeaves?: Record<string, unknown>
  /** True when this reading is (or, atomically, becomes) the profile weight. */
  mirroredToProfile: boolean
}

const PROFILE_LEAF_PREFIX = 'kidneyProfile.'

/**
 * Per-user tail of recordManualWeight's serial queue. Each link settles when its
 * batch has been ISSUED (never on server acknowledgement).
 */
const manualWeightQueue = new Map<string, Promise<void>>()

/**
 * Mobile FirebaseWeightService.recordManualWeight (4.38 F11): the day's manual
 * reading and the caller's profile leaves in ONE batch, so they land together or
 * not at all and `mirroredToProfile` is true of exactly what landed.
 *
 * 1. The day's manual row is looked up in the local cache (IndexedDB) — instant,
 *    offline too. A cache read that fails counts as "no row".
 * 2. One batch: update that row, or create one in the addWeight shape (local
 *    midnight timestamp), plus the profile leaves.
 * 3. The batch is committed WITHOUT waiting for the server: the SDK applies it
 *    to the local cache at once and keeps it in its persisted write queue.
 *
 * Same-tab saves run through a per-user queue released as soon as the batch is
 * issued, so the next save's cache lookup sees this save's row. No global
 * uniqueness: another device's uncached row or an old client can still leave two
 * manual rows on one date, as addWeight can.
 *
 * Returns the commit: resolves on server acknowledgement, rejects when the batch
 * (or building it) failed.
 */
export function recordManualWeight(uid: string, input: ManualWeightInput): Promise<void> {
  const previous = manualWeightQueue.get(uid) ?? Promise.resolve()
  let commit: Promise<void> | undefined

  const issued = previous.then(async () => {
    const leaves = input.profileLeaves ?? {}
    for (const path of Object.keys(leaves)) {
      if (!path.startsWith(PROFILE_LEAF_PREFIX) || path.length === PROFILE_LEAF_PREFIX.length) {
        throw new Error(`recordManualWeight: "${path}" is not a kidneyProfile leaf path`)
      }
    }
    if (!Number.isFinite(input.weightKg) || input.weightKg <= 0) {
      throw new Error('recordManualWeight: invalid weight')
    }

    const col = collection(db, 'kpal_users', uid, 'weightHistory')
    let existing: QueryDocumentSnapshot | null = null
    try {
      const cached = await getDocsFromCache(
        query(col, where('date', '==', input.date), where('source', '==', 'manual'), qlimit(1)),
      )
      existing = cached.docs[0] ?? null
    } catch (error) {
      // Never block the save on a lookup: worst case is a second manual row for
      // the day, which every reader already tolerates.
      console.warn('Weight cache lookup failed; adding a new reading:', error)
    }

    const batch = writeBatch(db)
    if (existing) {
      batch.update(existing.ref, {
        weightKg: input.weightKg,
        mirroredToProfile: input.mirroredToProfile,
        updatedAt: serverTimestamp(),
      })
    } else {
      // Same shape as addWeight (mobile FirebaseWeightService.addEntry).
      batch.set(doc(col), {
        userId: uid,
        weightKg: input.weightKg,
        date: input.date,
        source: 'manual',
        mirroredToProfile: input.mirroredToProfile,
        healthKitUUID: null,
        timestamp: Timestamp.fromDate(parseDateStr(input.date)),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    }
    if (Object.keys(leaves).length > 0) {
      batch.update(doc(db, 'kpal_users', uid), { ...leaves, updatedAt: serverTimestamp() })
    }

    commit = batch.commit()
    appEvents.emit(WEIGHT_HISTORY_CHANGED, uid)
    // A rejected batch is rolled back locally — let the history cache reload.
    commit.catch(() => appEvents.emit(WEIGHT_HISTORY_CHANGED, uid))
  })

  // Release the queue once the batch is issued (or the attempt failed before it).
  const released = issued.then(
    () => undefined,
    () => undefined,
  )
  manualWeightQueue.set(uid, released)
  void released.then(() => {
    if (manualWeightQueue.get(uid) === released) manualWeightQueue.delete(uid)
  })

  return issued.then(() => commit as Promise<void>)
}
